import { useState } from "react";
import { BiError } from "react-icons/bi";
import { BsShield } from "react-icons/bs";
import { TbAuth2Fa } from "react-icons/tb";
import { ImProfile } from "react-icons/im";
import { RiHistoryFill } from "react-icons/ri";
import { MdOutlineDevices } from "react-icons/md";
import { MdOutlineManageAccounts } from "react-icons/md";
import { MdDevices, MdOutlinePassword } from "react-icons/md";
import { Button, Card, Col, Container, Form, ListGroup, Row } from "react-bootstrap";
import LoginHistory from "./LoginHistory";
import LinkedDevices from "./LinkedDevices";
import UpdatePassword from "./UpdatePassword";
import ProfileDetails from "./ProfileDetails";
import UseToggle from "../../../Hooks/UseToggle";
import SetupAnotherDevice from "./SetupAnotherDevice";
import ManageAnotherAccount from "./ManageAnotherAccount";
import UpdateProfileName from "../Common/UpdateProfileName";
import BruteForceAttackRecord from "./BruteForceAttackRecord";
import TwoFactorAuthentocation from "./TwoFactorAuthentocation";

const ManageAccount = ({
  auth,
  currentUserData,
  isDark,
  setUpdator,
  organizationList,
}) => {
  const toggles = UseToggle();
  const [activeItem, setActiveItem] = useState("manageProfile");

  function getTwoInitials(name) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    const first = parts[0][0];
    const last = parts[parts.length - 1][0];
    return (first + last).toUpperCase();
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Form.Group as={Col} md={3} className="p-0 border-end">
            <div className="pb-1 pt-3 px-3">
              <h5 className="fw-bold mb-1">Manage Account</h5>
            </div>
            <div
              className="p-3 overflow-auto"
              style={{ height: "calc(100vh - 48px)" }}
            >
              <Card className="border rounded-1 mb-3">
                <Card.Body className="text-center">
                  <div className="hstack justify-content-center mb-2 position-relative">
                    <h6 className={`${isDark ? "bg-secondary" : "bg-primary-subtle"} p-3 rounded-1 fw-bold m-0`}>
                      {getTwoInitials(currentUserData?.name)}
                    </h6>
                    {currentUserData?.twoFAStatus &&
                      <BsShield className="text-primary position-absolute top-50 start-50 translate-middle" style={{ fontSize: 45, opacity: .3 }} />
                    }
                  </div>
                  <h6>{currentUserData?.name}</h6>
                  <Button
                    variant="outline-primary"
                    style={{ fontSize: 13 }}
                    title="Edit Details"
                    className={`rounded-1 py-1 px-3 mt-2 ${activeItem === "editDetails" && "btn-gradient text-white"}`}
                    onClick={() => toggles.setToggle3()}
                  >
                    Edit Details
                  </Button>
                </Card.Body>
              </Card>
              <ListGroup>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "manageProfile" && "btn-gradient text-white rounded-bottom-0"}`}
                  title="Manage Profile"
                  onClick={() => setActiveItem("manageProfile")}
                >
                  <ImProfile /> Manage Profile
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "changePassword" && "btn-gradient text-white rounded-0"}`}
                  title="Change Password"
                  onClick={() => setActiveItem("changePassword")}
                >
                  <MdOutlinePassword /> Change Password
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "2FA" && "btn-gradient text-white rounded-0"}`}
                  title="Two-Factor Authentication"
                  onClick={() => setActiveItem("2FA")}
                >
                  <TbAuth2Fa /> Two-Factor Authentication
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "setupAnotherDevice" && "btn-gradient text-white rounded-0"}`}
                  title="Setup Another Device"
                  onClick={() => setActiveItem("setupAnotherDevice")}
                >
                  <MdOutlineDevices /> Setup Another Device
                </ListGroup.Item>
                {organizationList?.length > 0 &&
                  <ListGroup.Item
                    action
                    style={{ fontSize: 13 }}
                    className={`hstack gap-2 ${activeItem === "manageAnotherAccount" && "btn-gradient text-white rounded-0"}`}
                    title="Manage Another Account"
                    onClick={() => setActiveItem("manageAnotherAccount")}
                  >
                    <MdOutlineManageAccounts /> Manage Another Account
                  </ListGroup.Item>
                }
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "devices" && "btn-gradient text-white rounded-0"}`}
                  title="Linked Devices"
                  onClick={() => setActiveItem("devices")}
                >
                  <MdDevices /> Linked Devices
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "loginHistory" && "btn-gradient text-white rounded-0"}`}
                  title="Login History"
                  onClick={() => setActiveItem("loginHistory")}
                >
                  <RiHistoryFill /> Login History
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  style={{ fontSize: 13 }}
                  className={`hstack gap-2 ${activeItem === "bruteForceAttackRecord" && "btn-gradient text-white rounded-top-0"}`}
                  title="Brute Force Attack"
                  onClick={() => setActiveItem("bruteForceAttackRecord")}
                >
                  <BiError /> Brute Force Attack
                </ListGroup.Item>
              </ListGroup>
            </div>
          </Form.Group>
          <Form.Group as={Col} md={9} className="p-0">
            {activeItem === "setupAnotherDevice" ? (
              <SetupAnotherDevice isDark={isDark} auth={auth} />
            ) : activeItem === "manageAnotherAccount" ? (
              <ManageAnotherAccount setUpdator={setUpdator} auth={auth} isDark={isDark} currentUserData={currentUserData} organizationList={organizationList} />
            ) : activeItem === "changePassword" ? (
              <UpdatePassword auth={auth} />
            ) : activeItem === "2FA" ? (
              <TwoFactorAuthentocation
                auth={auth}
                setUpdator={setUpdator}
                activeItem={activeItem}
              />
            ) : activeItem === "devices" ? (
              <LinkedDevices auth={auth} />
            ) : activeItem === "loginHistory" ? (
              <LoginHistory auth={auth} />
            ) : activeItem === "bruteForceAttackRecord" ? (
              <BruteForceAttackRecord auth={auth} />
            ) : (
              <ProfileDetails auth={auth} currentUserData={currentUserData} isDark={isDark} />
            )}
          </Form.Group>
        </Row>
      </Container>
      <UpdateProfileName
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        setUpdator={setUpdator}
        currentUserData={currentUserData}
        Title="Update Name"
        isDark={isDark}
        auth={auth}
      />
    </>
  );
};

export default ManageAccount;
